import { Outlet, useMatches } from "react-router";
import Header, { type HeaderVariant } from "~/components/header/Header";
import Footer, { type FooterMode } from "~/components/home/Footer";
import { useHideChrome } from "~/lib/hide-chrome";

// Pathless layout that gives otherwise-bare pages the standard site chrome (white
// nav on top, full footer below). Pages that already render their own Header/Footer —
// home, products/*, and the ported landing pages — are deliberately NOT nested under
// this in routes.ts, or they'd get a doubled nav and footer.
export default function SiteLayout() {
  // A page opts into header/footer variants with
  // `export const handle = { headerMode: "fleet", footerMode: "fleet" }`.
  const handles = useMatches()
    .map(
      (match) =>
        match.handle as
          | { headerMode?: HeaderVariant; footerMode?: FooterMode }
          | undefined,
    )
    .reverse();
  const headerMode = handles.find((h) => h?.headerMode)?.headerMode;
  const footerMode = handles.find((h) => h?.footerMode)?.footerMode;

  // Pages other apps embed (see lib/hide-chrome.ts) drop the header and footer. The
  // server omits them for `?hidechrome=1`; for the `#hidechrome` hash it can't know, so
  // they also render inside a `display: contents` [data-chrome] wrapper that app.css
  // hides before first paint when <html data-hide-chrome> is set.
  const hideChrome = useHideChrome();

  return (
    <>
      {!hideChrome && (
        <div data-chrome className="contents">
          <Header variant={headerMode} />
        </div>
      )}
      <Outlet />
      {!hideChrome && (
        <div data-chrome className="contents">
          <Footer mode={footerMode} />
        </div>
      )}
    </>
  );
}
